import { createElement } from "react";
import { useLocation } from "react-router-dom";
import { HistoryIcon, VipTopRightIcon } from "../components/Icon/IconSvgMap";
import { useNavigation } from "../utils";
import { translate } from "../i18n/utils";
import { navigationPolicy } from "../navigation/policy";
import {
  ROUTES,
  RECORDS_DEPOSIT_TABS,
  AGENT_CENTER_TABS,
} from "../types/routes";
// 页面配置接口
export interface PageConfig {
  title: string;
  icon?: React.ReactNode;
  description?: string;
}

// NavBar 右上角 Icon 统一 variant（绿色渐变 #42FFC3 → #C9FF66）
// const NAV_ICON_VARIANT = "green" as const;

export const usePageConfig = (): Record<string, PageConfig> => {
  const nav = useNavigation();
  const location = useLocation();

  // 从 URL 读取当前 AgentCenter 的 tab
  const getAgentCenterTab = () => {
    if (location.pathname !== ROUTES.AGENT_CENTER) {
      return AGENT_CENTER_TABS.COMMISSION; // 默认值
    }
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("tab") || AGENT_CENTER_TABS.COMMISSION;
  };

  const agentCenterTab = getAgentCenterTab();

  const base = {
    [ROUTES.RECHARGE]: {
      title: translate("navigation.deposit"),
      description: translate("navigation.depositDescription"),
      icon: createElement(HistoryIcon, {
        // variant: NAV_ICON_VARIANT,
        onClick: () => nav.navigate(`${ROUTES.RECORDS_DEPOSIT}`),
      }),
    },
    [ROUTES.RECHARGE_PROMO]: {
      title: translate("navigation.eventDetails"),
      description: translate("navigation.rechargePromoDescription"),
    },
    [ROUTES.EVENT_DETAILS]: {
      title: translate("navigation.eventDetails"),
      description: translate("navigation.eventDetailsDescription"),
    },
    [ROUTES.WITHDRAW]: {
      title: translate("navigation.withdraw"),
      description: translate("navigation.withdrawDescription"),
      icon: createElement(HistoryIcon, {
        // variant: NAV_ICON_VARIANT,
        onClick: () =>
          nav.navigate(
            `${ROUTES.RECORDS_DEPOSIT}?tabIndex=${RECORDS_DEPOSIT_TABS.WITHDRAW}`
          ),
      }),
    },
    [ROUTES.ACCOUNT_DETAILS]: {
      title: translate("navigation.accountDetails"),
      description: translate("navigation.accountDetailsDescription"),
    },
    [ROUTES.GAME_SEARCH]: {
      title: translate("navigation.search"),
      description: translate("navigation.searchDescription"),
    },
    [ROUTES.VIP]: {
      title: translate("navigation.vip"),
      description: translate("navigation.vipDescription"),
      icon: createElement(VipTopRightIcon, {
        onClick: () =>
          nav.navigate(
            `${ROUTES.RECORDS_DEPOSIT}?tabIndex=${RECORDS_DEPOSIT_TABS.BONUS}`
          ),
      }),
    },
    [ROUTES.RECORDS_DEPOSIT]: {
      title: translate("navigation.records"),
      description: translate("navigation.recordsDescription"),
    },
    [ROUTES.PROFILE]: {
      title: translate("navigation.profile"),
      description: translate("navigation.profileDescription"),
    },
    [ROUTES.LOGIN]: {
      title: translate("navigation.login"),
      description: translate("navigation.loginDescription"),
    },
    [ROUTES.REGISTER]: {
      title: translate("navigation.register"),
      description: translate("navigation.registerDescription"),
    },
    [ROUTES.REGISTER_NEW]: {
      title: translate("navigation.register"),
      description: translate("navigation.registerDescription"),
    },
    [ROUTES.EVENT_CENTER]: {
      title: translate("navigation.eventCenter"),
      description: translate("navigation.eventCenterDescription"),
    },
    // 实时反水「反水比例」档位页：PageWrapper 顶部 NavBar（返回箭头 + 标题）
    [ROUTES.REALTIME_REBATE_TIERS]: {
      title: translate("realtimeRebate.tierPageTitle"),
    },
    // 活动中心·六 Tab 版（supergol 底栏 Promoção 落点）
    [ROUTES.AGENT_CENTER]: {
      title: translate("navigation.agentCenter"),
      description: translate("navigation.agentCenterDescription"),
      icon: (() => {
        if (agentCenterTab === AGENT_CENTER_TABS.COMMISSION) {
          return createElement(HistoryIcon, {
            // variant: NAV_ICON_VARIANT,
            onClick: () => nav.navigate(ROUTES.AGENT_COMMISSION),
          });
        } else if (agentCenterTab === AGENT_CENTER_TABS.TREASURE) {
          return createElement(HistoryIcon, {
            // variant: NAV_ICON_VARIANT,
            onClick: () => nav.navigate(`${ROUTES.RECORDS_DEPOSIT}?tabIndex=4`),
          });
        }
        return;
      })(),
    },
    [ROUTES.INVITE_FRIENDS]: {
      title: translate("navigation.inviteFriends"),
      description: translate("navigation.inviteFriendsDescription"),
    },
    [ROUTES.AGENT_COMMISSION]: {
      title: translate("navigation.agentRecords"),
      description: translate("navigation.agentRecordsDescription"),
      // icon: createElement(HistoryIcon, {
      //   onClick: () => nav.navigate(ROUTES.AGENT_COMMISSION),
      // }),
    },
    [ROUTES.AGENT_PANEL]: {
      title: translate("agentPanel.title"),
    },
    [ROUTES.REDEEM]: {
      title: translate("navigation.redeem"),
      description: translate("navigation.redeemDescription"),
      icon: createElement(HistoryIcon, {
        // variant: NAV_ICON_VARIANT,
        onClick: () => nav.navigate(`${ROUTES.RECORDS_DEPOSIT}?tabIndex=4`),
      }),
    },
    [ROUTES.PROMOTION_DETAILS]: {
      title: translate("navigation.details"),
      description: translate("navigation.detailsDescription"),
    },
    [ROUTES.MESSAGE_CENTER]: {
      title: translate("navigation.messageCenter"),
      description: translate("navigation.messageCenterDescription"),
    },
    [ROUTES.PAY_RECORDS]: {
      title: translate("sgPay.depositRecord"),
      description: translate("sgPay.depositRecord"),
    },
    [ROUTES.AUDIT_DETAIL]: {
      title: translate("sgPay.auditDetail"),
      description: translate("sgPay.auditDetail"),
    },
    [ROUTES.SECURITY_CENTER]: {
      title: translate("profile.securityCenter"),
      description: translate("profile.securityCenter"),
    },
    [ROUTES.SECURITY_VERIFY_PASSWORD]: {
      title: translate("security.verifyTitle"),
      description: translate("security.verifyLoginPassword"),
    },
    [ROUTES.SECURITY_CHANGE_PASSWORD]: {
      title: translate("security.changeTitle"),
      description: translate("security.confirmNewPassword"),
    },
    [ROUTES.SECURITY_BIND_EMAIL]: {
      title: translate("security.newEmailTitle"),
      description: translate("security.confirmNewEmail"),
    },
    [ROUTES.MESSAGE]: {
      title: translate("navigation.message"),
      description: translate("navigation.messageDescription"),
    },
    [ROUTES.COMPONENT_TEST]: {
      title: translate("navigation.componentTest"),
      description: translate("navigation.componentTestDescription"),
    },
    [ROUTES.MAINTAIN]: {
      title: translate("navigation.maintain"),
      description: translate("navigation.maintainDescription"),
    },
    [ROUTES.REPAIR]: {
      title: translate("navigation.repair"),
      description: translate("navigation.repairDescription"),
    },
    [ROUTES.UNPAID_ORDERS]: {
      title: translate("unpaidOrders.pageTitle"),
      description: translate("unpaidOrders.pageDescription"),
      icon: createElement(HistoryIcon, {
        variant: "golden",
        onClick: () => nav.navigate(`${ROUTES.RECORDS_DEPOSIT}`),
      }),
    },
  } as Record<string, PageConfig>;
  // navigation 轴 policy 可按路由覆盖标题（只换文案，不动右侧图标）
  const overrides = navigationPolicy.pageTitleKeyOverrides;
  if (overrides) {
    for (const [route, key] of Object.entries(overrides)) {
      base[route] = { ...(base[route] ?? {}), title: translate(key) };
    }
  }
  return base;
};
